import React, { useState, useMemo } from 'react';
import { FaShoppingCart, FaTruck, FaBoxOpen, FaCheckCircle, FaPlus, FaTrash, FaExternalLinkAlt } from 'react-icons/fa';
import { useOrders, addToCollection, updateInCollection, deleteFromCollection } from '../hooks/useFirestore';
import { useFinance } from '../context/FinanceContext';

const SOURCES = ['Amazon', 'Flipkart', 'Myntra', 'Swiggy', 'Zomato', 'Other'];
const STATUSES = ['ordered', 'shipped', 'out_for_delivery', 'delivered'];
const STATUS_LABELS = { ordered: 'Ordered', shipped: 'Shipped', out_for_delivery: 'Out for Delivery', delivered: 'Delivered' };
const STATUS_PROGRESS = { ordered: 25, shipped: 50, out_for_delivery: 75, delivered: 100 };

const OrderTracker = () => {
    const { data: orders } = useOrders();
    const { settings } = useFinance();
    const [showAddForm, setShowAddForm] = useState(false);

    const activeOrders = useMemo(() => (orders || []).filter(o => o.status !== 'delivered'), [orders]);
    const deliveredOrders = useMemo(() => (orders || []).filter(o => o.status === 'delivered'), [orders]);
    const hasData = (orders && orders.length > 0);

    // Monthly shopping total
    const monthlyTotal = useMemo(() => {
        const currentMonth = new Date().toISOString().slice(0, 7);
        return (orders || [])
            .filter(o => (o.orderDate || '').startsWith(currentMonth))
            .reduce((s, o) => s + (Number(o.price) || 0), 0);
    }, [orders]);

    const shoppingCap = settings?.shoppingCap || 0;
    const spendPercent = shoppingCap > 0 ? Math.min(100, (monthlyTotal / shoppingCap) * 100) : 0;

    const handleStatusChange = async (order, newStatus) => {
        await updateInCollection('orders', order.id, { status: newStatus });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this order?')) return;
        await deleteFromCollection('orders', id);
    };

    const handleAddOrder = async (data) => {
        await addToCollection('orders', data);
        setShowAddForm(false);
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <p className="text-[10px] font-semibold text-accent-400 uppercase tracking-[0.2em] mb-1">Shopping</p>
                    <h1 className="text-2xl font-bold tracking-tight text-frost-900">Order Tracker</h1>
                    <p className="text-frost-500 mt-0.5 text-sm">Monitor shopping limits and delivery status</p>
                </div>
                <button onClick={() => setShowAddForm(true)} className="btn-pill btn-glow flex items-center gap-2 text-xs">
                    <FaPlus className="text-[10px]" /> Add Order
                </button>
            </div>

            {/* Monthly Shopping Summary */}
            {shoppingCap > 0 && (
                <div className="glass-card p-6">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-frost-900">Monthly Shopping</h3>
                        <span className="text-xs font-semibold text-frost-400">₹{monthlyTotal.toLocaleString()} / ₹{shoppingCap.toLocaleString()}</span>
                    </div>
                    <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-1000 ${spendPercent > 80 ? 'bg-gold-500' : 'bg-gradient-to-r from-accent-500 to-accent-400'} shadow-glow`}
                            style={{ width: `${spendPercent}%` }}></div>
                    </div>
                    <p className="text-[10px] text-frost-400 mt-2">
                        {spendPercent >= 100 ? 'Shopping cap exceeded!' : spendPercent >= 80 ? 'Approaching shopping cap' : `${(100 - spendPercent).toFixed(0)}% of budget remaining`}
                    </p>
                </div>
            )}

            {!hasData ? (
                <div className="glass-card p-12 flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-4">
                        <FaShoppingCart className="text-xl text-frost-400" />
                    </div>
                    <h3 className="text-base font-bold text-frost-900 mb-1">No orders tracked</h3>
                    <p className="text-sm text-frost-500 max-w-md">Start adding online orders to track deliveries and monitor spending</p>
                    <button onClick={() => setShowAddForm(true)} className="mt-5 btn-pill btn-glow flex items-center gap-2 text-xs">
                        <FaPlus /> Add Order
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-5">
                        {/* Active Orders */}
                        {activeOrders.length > 0 && (
                            <>
                                <div className="flex items-center justify-between">
                                    <h3 className="text-base font-bold flex items-center gap-3">
                                        <FaTruck className="text-accent-400" /> Active Shipments
                                    </h3>
                                    <span className="text-xs text-frost-400">{activeOrders.length} Items</span>
                                </div>
                                {activeOrders.map(order => (
                                    <DeliveryCard key={order.id} order={order}
                                        onStatusChange={(status) => handleStatusChange(order, status)}
                                        onDelete={() => handleDelete(order.id)} />
                                ))}
                            </>
                        )}

                        {/* Delivered */}
                        {deliveredOrders.length > 0 && (
                            <div className="glass-card p-6">
                                <h3 className="text-base font-bold mb-4 flex items-center gap-3">
                                    <FaBoxOpen className="text-mint-400" /> Recent Deliveries
                                </h3>
                                <div className="space-y-3">
                                    {deliveredOrders.map(order => (
                                        <HistoryItem key={order.id} order={order} onDelete={() => handleDelete(order.id)} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Stats sidebar */}
                    <div className="space-y-4">
                        <div className="glass-card p-6 text-center">
                            <p className="text-[10px] text-frost-400 uppercase tracking-widest mb-2">Total Orders</p>
                            <p className="text-3xl font-bold gradient-text">{orders.length}</p>
                        </div>
                        <div className="glass-card p-6 text-center">
                            <p className="text-[10px] text-frost-400 uppercase tracking-widest mb-2">Active</p>
                            <p className="text-3xl font-bold text-accent-400">{activeOrders.length}</p>
                        </div>
                        <div className="glass-card p-6 text-center">
                            <p className="text-[10px] text-frost-400 uppercase tracking-widest mb-2">Total Spent</p>
                            <p className="text-xl font-bold text-frost-900">₹{(orders || []).reduce((s, o) => s + (Number(o.price) || 0), 0).toLocaleString()}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Order Modal */}
            {showAddForm && <AddOrderModal onSave={handleAddOrder} onClose={() => setShowAddForm(false)} />}
        </div>
    );
};

const DeliveryCard = ({ order, onStatusChange, onDelete }) => {
    const progress = STATUS_PROGRESS[order.status] || 25;
    const daysUntil = order.expectedDelivery ? Math.ceil((new Date(order.expectedDelivery) - new Date()) / 86400000) : null;

    return (
        <div className="glass-card p-6 group">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/[0.03] text-frost-400 border border-white/[0.06] flex items-center justify-center text-xl">
                        <FaBoxOpen />
                    </div>
                    <div>
                        <h4 className="font-bold text-frost-900">{order.itemName}</h4>
                        <p className="text-xs text-accent-400 mt-0.5">{order.source} &bull; ₹{Number(order.price).toLocaleString()}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {daysUntil !== null && (
                        <div className="text-right">
                            <p className="text-[10px] text-frost-400 uppercase tracking-widest mb-0.5">ETA</p>
                            <p className="text-sm font-bold text-frost-900">{daysUntil > 0 ? `${daysUntil}d` : 'Today'}</p>
                        </div>
                    )}
                    <select value={order.status} onChange={e => onStatusChange(e.target.value)}
                        className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-2 py-1 text-[10px] text-frost-900 focus:outline-none">
                        {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                    <button onClick={onDelete} className="p-1.5 text-frost-400 hover:text-gold-400 opacity-0 group-hover:opacity-100 transition-all">
                        <FaTrash className="text-[10px]" />
                    </button>
                </div>
            </div>
            <div className="mt-4 space-y-2">
                <div className="flex justify-between text-[10px] text-frost-400">
                    <span>{STATUS_LABELS[order.status]}</span>
                    <span>{progress}%</span>
                </div>
                <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-accent-500 to-accent-400 shadow-glow transition-all duration-1000" style={{ width: `${progress}%` }}></div>
                </div>
            </div>
            {order.trackingUrl && (
                <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 text-[10px] text-accent-400 hover:text-accent-300">
                    <FaExternalLinkAlt className="text-[8px]" /> Track Shipment
                </a>
            )}
        </div>
    );
};

const HistoryItem = ({ order, onDelete }) => (
    <div className="flex items-center justify-between p-4 hover:bg-white/[0.02] rounded-xl transition-all group">
        <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-mint-500/10 rounded-lg flex items-center justify-center text-mint-400">
                <FaCheckCircle className="text-sm" />
            </div>
            <div>
                <h5 className="text-sm font-semibold text-frost-900">{order.itemName}</h5>
                <p className="text-[10px] text-frost-400 mt-0.5">{order.source} &bull; {order.orderDate?.slice(0, 10)}</p>
            </div>
        </div>
        <div className="flex items-center gap-3">
            <div className="text-right">
                <p className="text-sm font-bold text-frost-900">₹{Number(order.price).toLocaleString()}</p>
                <p className="text-[10px] text-mint-400 mt-0.5">Delivered</p>
            </div>
            <button onClick={onDelete} className="p-1.5 text-frost-400 hover:text-gold-400 opacity-0 group-hover:opacity-100 transition-all">
                <FaTrash className="text-[10px]" />
            </button>
        </div>
    </div>
);

const AddOrderModal = ({ onSave, onClose }) => {
    const [form, setForm] = useState({
        itemName: '', price: '', source: 'Amazon', orderDate: new Date().toISOString().slice(0, 10),
        expectedDelivery: '', trackingUrl: '', status: 'ordered',
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.itemName || !form.price) return;
        setSaving(true);
        await onSave({ ...form, price: Number(form.price) });
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-[200] bg-dark-700/80 backdrop-blur-xl flex items-center justify-center p-6">
            <div className="bg-dark-500 w-full max-w-lg rounded-2xl p-8 shadow-card-hover border border-white/[0.06] space-y-6">
                <h3 className="text-lg font-bold text-frost-900">Add Order</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="text" placeholder="Item name" value={form.itemName} onChange={e => setForm({...form, itemName: e.target.value})} required
                        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                    <div className="grid grid-cols-2 gap-4">
                        <input type="number" placeholder="Price" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required
                            className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                        <select value={form.source} onChange={e => setForm({...form, source: e.target.value})}
                            className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none">
                            {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">Order Date</label>
                            <input type="date" value={form.orderDate} onChange={e => setForm({...form, orderDate: e.target.value})}
                                className="w-full mt-1 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none" />
                        </div>
                        <div>
                            <label className="text-[10px] font-medium text-frost-400 uppercase tracking-widest">Expected Delivery</label>
                            <input type="date" value={form.expectedDelivery} onChange={e => setForm({...form, expectedDelivery: e.target.value})}
                                className="w-full mt-1 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none" />
                        </div>
                    </div>
                    <input type="url" placeholder="Tracking URL (optional)" value={form.trackingUrl} onChange={e => setForm({...form, trackingUrl: e.target.value})}
                        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30" />
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="flex-1 py-3 border border-white/[0.06] text-frost-500 font-bold rounded-xl hover:bg-white/[0.03] text-sm">Cancel</button>
                        <button type="submit" disabled={saving} className="flex-1 py-3 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold rounded-xl shadow-glow text-sm">
                            {saving ? 'Saving...' : 'Add Order'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OrderTracker;
